import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { PlusIcon, SendIcon } from 'lucide-react';
import { formatCurrency } from './pharmacyUtils';
type DraftLine = {
  medicineId: string;
  quantity: string;
  unitCost: string;
};
export function PurchaseOrders() {
  const {
    suppliers,
    medicines,
    purchaseOrders,
    createPurchaseOrder,
    updatePurchaseOrder,
    currentUser
  } = useData();
  const [showForm, setShowForm] = useState(false);
  const [supplierId, setSupplierId] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([{
    medicineId: '',
    quantity: '',
    unitCost: ''
  }]);
  const [error, setError] = useState('');
  const addLine = () => setLines([...lines, {
    medicineId: '',
    quantity: '',
    unitCost: ''
  }]);
  const updateLine = (index: number, key: keyof DraftLine, value: string) => setLines(lines.map((line, itemIndex) => itemIndex === index ? {
    ...line,
    [key]: value
  } : line));
  const save = (status: 'Draft' | 'Pending') => {
    const supplier = suppliers.find(item => item.id === supplierId);
    const parsedLines = lines.filter(line => line.medicineId && Number(line.quantity) > 0 && Number(line.unitCost) >= 0).map(line => {
      const medicine = medicines.find(item => item.id === line.medicineId)!;
      return {
        medicineId: medicine.id,
        medicineName: medicine.name,
        quantityOrdered: Number(line.quantity),
        quantityReceived: 0,
        unitCost: Number(line.unitCost)
      };
    });
    if (!supplier || !parsedLines.length) {
      setError('Select a supplier and add at least one valid medicine line.');
      return;
    }
    createPurchaseOrder({
      supplierId: supplier.id,
      supplierName: supplier.name,
      status,
      orderedAt: new Date().toISOString(),
      expectedDate: expectedDate || undefined,
      notes: notes || undefined,
      lines: parsedLines,
      createdBy: currentUser?.id || ''
    });
    setShowForm(false);
    setSupplierId('');
    setExpectedDate('');
    setNotes('');
    setLines([{
      medicineId: '',
      quantity: '',
      unitCost: ''
    }]);
    setError('');
  };
  return <div className="space-y-5" data-id="element-4499">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-4500">
        <div data-id="element-4501">
          <h2 className="text-xl font-bold text-slate-950" data-id="element-4502">Purchase Orders</h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-4503">
            Receive stock against a purchase order to update outstanding lines
            automatically.
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-teal-600 hover:bg-teal-700" data-id="element-4504">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-4505" /> New purchase order
        </Button>
      </div>
      {showForm && <section className="rounded-xl border border-teal-200 bg-teal-50/50 p-5" data-id="element-4506">
          <div className="mb-4 flex justify-between" data-id="element-4507">
            <h3 className="font-bold text-slate-900" data-id="element-4508">New purchase order</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-sm font-semibold text-slate-500 hover:text-slate-900" data-id="element-4509">
              Cancel
            </button>
          </div>
          {error && <p className="mb-3 text-sm text-red-600" data-id="element-4510">{error}</p>}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-4511">
            <Select label="Supplier" value={supplierId} onChange={e => setSupplierId(e.target.value)} options={suppliers.filter(supplier => supplier.status === 'Active').map(supplier => ({
          value: supplier.id,
          label: supplier.name
        }))} data-id="element-4512" />
            <Input label="Expected date" type="date" value={expectedDate} onChange={e => setExpectedDate(e.target.value)} data-id="element-4513" />
          </div>
          <div className="mt-4 overflow-x-auto" data-id="element-4514">
            <table className="min-w-full" data-id="element-4515">
              <thead data-id="element-4516">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-4517">
                  <th className="pb-2" data-id="element-4518">Medicine</th>
                  <th className="pb-2" data-id="element-4519">Quantity</th>
                  <th className="pb-2" data-id="element-4520">Unit cost</th>
                </tr>
              </thead>
              <tbody data-id="element-4521">
                {lines.map((line, index) => <tr key={index} data-id="element-4522">
                    <td className="pr-3 pb-2 min-w-64" data-id="element-4523">
                      <select aria-label="Medicine" value={line.medicineId} onChange={e => updateLine(index, 'medicineId', e.target.value)} className="w-full rounded-md border border-slate-300 bg-white p-2 text-sm" data-id="element-4524">
                        <option value="" data-id="element-4525">Select medicine</option>
                        {medicines.map(medicine => <option key={medicine.id} value={medicine.id} data-id="element-4526">
                            {medicine.name}
                          </option>)}
                      </select>
                    </td>
                    <td className="pr-3 pb-2" data-id="element-4527">
                      <input aria-label="Quantity" type="number" min="1" value={line.quantity} onChange={e => updateLine(index, 'quantity', e.target.value)} className="w-28 rounded-md border border-slate-300 p-2 text-sm" data-id="element-4528" />
                    </td>
                    <td className="pb-2" data-id="element-4529">
                      <input aria-label="Unit cost" type="number" min="0" step="0.01" value={line.unitCost} onChange={e => updateLine(index, 'unitCost', e.target.value)} className="w-28 rounded-md border border-slate-300 p-2 text-sm" data-id="element-4530" />
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={addLine} className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-700" data-id="element-4531">
            + Add medicine line
          </button>
          <label className="mt-4 block text-sm font-medium text-gray-700" data-id="element-4532">
            Notes
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="mt-1 block w-full rounded-md border border-gray-300 p-2 text-sm" data-id="element-4533" />
          </label>
          <div className="mt-4 flex justify-end gap-2" data-id="element-4534">
            <Button variant="outline" onClick={() => save('Draft')} data-id="element-4535">
              Save draft
            </Button>
            <Button onClick={() => save('Pending')} className="bg-teal-600 hover:bg-teal-700" data-id="element-4536">
              <SendIcon className="mr-2 h-4 w-4" data-id="element-4537" /> Submit order
            </Button>
          </div>
        </section>}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4538">
        <div className="overflow-x-auto" data-id="element-4539">
          <table className="min-w-full divide-y divide-slate-100" data-id="element-4540">
            <thead className="bg-slate-50" data-id="element-4541">
              <tr data-id="element-4542">
                {['Order', 'Supplier', 'Status', 'Expected', 'Value', 'Actions'].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-4543">
                    {heading}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-4544">
              {purchaseOrders.map(order => <tr key={order.id} data-id="element-4545">
                  <td className="px-5 py-3.5 font-semibold text-slate-900" data-id="element-4546">
                    {order.orderNumber}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4547">
                    {order.supplierName}
                  </td>
                  <td className="px-5 py-3.5" data-id="element-4548">
                    <Status status={order.status} data-id="element-4549" />
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4550">
                    {order.expectedDate ? new Date(`${order.expectedDate}T00:00:00`).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4551">
                    {formatCurrency(order.lines.reduce((sum, line) => sum + line.quantityOrdered * line.unitCost, 0))}
                  </td>
                  <td className="px-5 py-3.5" data-id="element-4552">
                    {order.status === 'Draft' && <button type="button" onClick={() => updatePurchaseOrder(order.id, {
                  status: 'Pending'
                })} className="text-sm font-semibold text-blue-600 hover:text-blue-700" data-id="element-4553">
                        Submit
                      </button>}
                  </td>
                </tr>)}
              {!purchaseOrders.length && <tr data-id="element-4554">
                  <td colSpan={6} className="px-5 py-14 text-center text-sm text-slate-500" data-id="element-4555">
                    No purchase orders yet.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>;
}
function Status({
  status
}: {
  status: string;
}) {
  const colors: Record<string, string> = {
    Draft: 'bg-slate-100 text-slate-700',
    Pending: 'bg-blue-50 text-blue-700',
    'Partially Received': 'bg-amber-50 text-amber-700',
    Received: 'bg-emerald-50 text-emerald-700',
    Cancelled: 'bg-red-50 text-red-700'
  };
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${colors[status]}`} data-id="element-4556">
      {status}
    </span>;
}